import axios from 'axios';
import {
    clearFrontendAuthSession,
    getCurrentLoginUrl,
} from '@/utils/frontendAuthSession';
import { markMachineTranslationDetected } from '@/utils/machineTranslationNotice';

const authenticatedApiClient = axios.create({
    baseURL: process.env.VUE_APP_BACKEND_API_URL || 'http://localhost:3000/api',
    headers: {
        'X-API-KEY': process.env.VUE_APP_API_KEY,
    },
});

let isRedirectingToLogin = false;

function getStoredAuthToken() {
    if (typeof window === 'undefined') {
        return null;
    }

    try {
        return window.localStorage.getItem('authToken')
            || window.sessionStorage.getItem('authToken');
    } catch {
        return null;
    }
}

export function getCurrentRequestLocale() {
    if (typeof window === 'undefined') return 'nl';
    const routeLocale = window.location.pathname.match(/^\/(nl|en|id)(?:\/|$)/)?.[1];
    if (routeLocale) return routeLocale;
    try {
        const storedLocale = window.localStorage.getItem('locale');
        return ['nl', 'en', 'id'].includes(storedLocale) ? storedLocale : 'nl';
    } catch {
        return 'nl';
    }
}

export function installRequestContextInterceptors(client) {
    client.interceptors.request.use((config) => {
        config.params = {
            ...(config.params || {}),
            locale: getCurrentRequestLocale(),
        };
        return config;
    });

    client.interceptors.response.use((response) => {
        const machineTranslated = response?.headers?.get?.('x-machine-translated')
            ?? response?.headers?.['x-machine-translated'];
        if (String(machineTranslated || '').toLowerCase() === 'true') {
            markMachineTranslationDetected();
        }
        return response;
    });

    return client;
}

export function installAuthenticationInterceptors(client) {
    installRequestContextInterceptors(client);
    client.interceptors.request.use((config) => {
        const token = getStoredAuthToken();
        if (token) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    });

    client.interceptors.response.use(
        response => response,
        (error) => {
            if (
                error?.response?.status === 401
                && typeof window !== 'undefined'
                && !isRedirectingToLogin
            ) {
                isRedirectingToLogin = true;
                clearFrontendAuthSession();
                window.location.assign(getCurrentLoginUrl());
            }

            return Promise.reject(error);
        }
    );

    return client;
}

installAuthenticationInterceptors(authenticatedApiClient);

export default authenticatedApiClient;
